import { observable, makeObservable } from 'mobx'
import _ from 'lodash'
import axios from 'axios'
import { errorDetails } from 'constants.js'
import { removeHtml } from 'utils.js'
import { uiStore } from './UIStore'
import { logout, redirectToExpired, redirectToExpired24 } from '../utils'
import i18next from '../i18n'

const isGoogleOrPoiApiError = (e) => {
  if (!e) return false
  const url = String(
    _.get(e, 'config.url') ||
    _.get(e, 'response.config.url') ||
    _.get(e, 'request.responseURL') ||
    _.get(e, 'url') ||
    ''
  ).toLowerCase()

  if (
    url.includes('poi/') ||
    url.includes('googleapis.com') ||
    url.includes('google') ||
    url.includes('places')
  ) {
    return true
  }

  const responseData = _.get(e, 'response.data') || {}
  const errorMessage = String(
    responseData.error ||
    responseData.message ||
    _.get(e, 'message') ||
    ''
  ).toLowerCase()

  if (
    errorMessage.includes('google') ||
    errorMessage.includes('place details') ||
    errorMessage.includes('place_id') ||
    errorMessage.includes('placeid') ||
    errorMessage.includes('places') ||
    errorMessage.includes('autocomplete')
  ) {
    return true
  }

  return false
}

// Stores the current error to show in the error bar,
// and allows the user to clear it
class ErrorStore {
  @observable errorMessage = null

  constructor () {
    makeObservable(this)
  }

  logError (message, e) {
    if (window.Rollbar) {
      window.Rollbar.error(message, e, e.response || e.request)
    } else {
      console.warn(message, e, e.response || e.request)
    }
  }

  error (e) {
    // Ignore cancelled requests
    if (axios.isCancel(e)) return

    // If Google Places or POI APIs fail, do not show error banner or clear selection
    if (isGoogleOrPoiApiError(e)) {
      console.warn('[ErrorStore] Google / POI API error suppressed:', e)
      return
    }

    // log user out if not authorized
    if (_.get(e, 'response.status') === 401) {
      return logout()
    }

    // log the user out if we get access denied from the API
    if (_.get(e, 'response.status') === 403) {
      if (_.get(e, 'response.data.detail') === errorDetails.EXPIRED) {
        return redirectToExpired()
      } else if (_.get(e, 'response.data.detail') === errorDetails.EXPIRED_24_TRIAL) {
        return redirectToExpired24()
      } else {
        return logout()
      }
    }

    // if error 404 after target group deleted ignore this error
    if (_.get(e, 'response.status') === 404 &&
      _.get(e, 'response.config.url') &&
      _.get(e, 'response.config.url').indexOf('api/geodata/target-group/') >= 0) {
      return
    }

    let error = i18next.t('GLOBAL.AN_ERROR_OCCURRED', { interpolation: { escapeValue: false } })

    // show specific error from response data without wiping selection
    if (_.get(e, 'response.status') === 400) {
      uiStore.processing = false
      const responseData = _.get(e, 'response.data')
      if (responseData && typeof responseData === 'object') {
        const firstKey = Object.keys(responseData)[0]
        if (firstKey) {
          const val = responseData[firstKey]
          error = removeHtml(Array.isArray(val) ? val[0] : String(val))
        }
      }
      this.errorMessage = error
      this.logError(error, e)
      return
    }

    // show error only if it is not recognized
    this.errorMessage = error
    this.logError(error, e)
  }

  clear () {
    this.errorMessage = null
  }
}

const errorStore = new ErrorStore()

// decorator which catches any errors thrown in a function
export function catchError (target, key, descriptor) {
  const original = descriptor.value
  descriptor.value = async function (...args) {
    try {
      return await original.apply(this, args)
    } catch (e) {
      errorStore.error(e)
    }
  }
  return descriptor
}

export default errorStore
